import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Download, Share2, Copy, Check, BookOpen } from "lucide-react";
import html2canvas from "html2canvas";

/**
 * Beautiful shareable card for the weekly verse.
 * Rendered as HTML/CSS and exported to PNG via html2canvas.
 */
export default function VerseCard({ verse, onClose }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const shareText = `« ${verse.text} » — ${verse.ref}\n\n🙏 Verset du dimanche · Jesus Talk`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Verset copié !");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `jesus-talk-verset-${verse.ref.replace(/\s|:/g, "-").toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Image téléchargée !");
    } catch (e) {
      toast.error("Impossible de générer l'image");
    } finally {
      setBusy(false);
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
      } catch { /* ignored */ }
    } else {
      copy();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-[#0A0806]/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="verse-card-modal"
    >
      <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label="Fermer"
          data-testid="verse-card-close"
          className="absolute -top-3 -right-3 h-10 w-10 rounded-full bg-white text-[#2E4959] hover:bg-[#F4E4C1] flex items-center justify-center jt-medium-shadow z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Shareable card */}
        <div
          ref={cardRef}
          data-testid="verse-card-image"
          className="relative aspect-[4/5] rounded-3xl overflow-hidden jt-medium-shadow"
          style={{ background: "linear-gradient(160deg, #2E4959 0%, #4A6D82 50%, #7B98A8 100%)" }}
        >
          {/* Decorative glows */}
          <div className="absolute -left-16 -top-16 w-64 h-64 rounded-full bg-[#C99A5B]/25 blur-3xl" />
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[#F4E4C1]/15 blur-3xl" />
          <div className="absolute inset-0 opacity-15 jt-grain" />

          {/* Content */}
          <div className="relative h-full flex flex-col justify-between p-8 md:p-10 text-white">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.35em] text-[#F4E4C1]">Verset du dimanche</span>
              <BookOpen className="w-5 h-5 text-[#F4E4C1]" strokeWidth={1.5} />
            </div>

            <div className="flex-1 flex items-center">
              <blockquote className="font-serif text-2xl md:text-3xl leading-[1.25] text-white">
                <span className="text-4xl text-[#F4E4C1] leading-none">« </span>
                {verse.text}
                <span className="text-4xl text-[#F4E4C1] leading-none"> »</span>
              </blockquote>
            </div>

            <div>
              <p className="font-serif italic text-lg text-[#F4E4C1] mb-4">— {verse.ref}</p>
              <div className="flex items-center justify-between pt-4 border-t border-white/20">
                <span className="font-display text-lg tracking-widest text-white">JESUS TALK</span>
                <span className="text-[10px] uppercase tracking-widest text-white/70">jesustalk.fr</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mt-4 justify-center">
          <button
            onClick={download}
            disabled={busy}
            data-testid="verse-card-download"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#2E4959] text-sm hover:bg-[#F4E4C1] transition disabled:opacity-60"
          >
            <Download className="w-4 h-4" /> {busy ? "..." : "Télécharger"}
          </button>
          <button
            onClick={shareNative}
            data-testid="verse-card-share"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4A6D82] text-white text-sm hover:bg-[#2E4959] transition"
          >
            <Share2 className="w-4 h-4" /> Partager
          </button>
          <button
            onClick={copy}
            data-testid="verse-card-copy"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white text-sm hover:bg-white/20 transition"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copié" : "Copier le texte"}
          </button>
        </div>
      </div>
    </div>
  );
}
